import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check } from 'lucide-react';
import { pricingPlans } from '../mock';
import { Button } from './ui/button';

export const Pricing = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.2 });
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <section
      id="pricing"
      ref={containerRef}
      className="py-24 relative overflow-hidden"
      style={{ backgroundColor: '#f7f5f2' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ color: '#283750' }}
          >
            Simple, Transparent
            <br />
            <span style={{ color: '#61525a' }}>Pricing</span>
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: '#736c64' }}
          >
            Choose the perfect plan for your event management needs. All plans
            include our core features.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{
                opacity: isInView ? 1 : 0,
                y: isInView ? 0 : 50,
              }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              onHoverStart={() => setHoveredCard(plan.id)}
              onHoverEnd={() => setHoveredCard(null)}
              whileHover={{
                y: -15,
                transition: { duration: 0.3 },
              }}
              className={`relative p-8 rounded-2xl transition-all duration-500 ${
                plan.highlighted ? 'md:scale-110 z-10' : ''
              }`}
              style={{
                backgroundColor: 'white',
                boxShadow:
                  hoveredCard === plan.id
                    ? '0 25px 50px rgba(0, 0, 0, 0.2)'
                    : '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: plan.highlighted
                  ? `3px solid ${plan.color}`
                  : '1px solid transparent',
              }}
            >
              {/* Popular Badge */}
              {plan.highlighted && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-bold"
                  style={{ backgroundColor: plan.color }}
                >
                  MOST POPULAR
                </motion.div>
              )}

              {/* Plan Name */}
              <h3
                className="text-2xl font-bold mb-2"
                style={{ color: '#283750' }}
              >
                {plan.name}
              </h3>
              <p className="text-sm mb-6" style={{ color: '#736c64' }}>
                {plan.description}
              </p>

              {/* Price */}
              <div className="mb-8">
                {plan.price === 'Custom' ? (
                  <div>
                    <span
                      className="text-4xl font-bold"
                      style={{ color: plan.color }}
                    >
                      Custom
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline">
                    <span
                      className="text-5xl font-bold"
                      style={{ color: plan.color }}
                    >
                      ${plan.price}
                    </span>
                    <span className="ml-2" style={{ color: '#736c64' }}>
                      /{plan.period}
                    </span>
                  </div>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <motion.li
                    key={featureIndex}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{
                      opacity: isInView ? 1 : 0,
                      x: isInView ? 0 : -10,
                    }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.2 + featureIndex * 0.05,
                    }}
                    className="flex items-start space-x-3"
                  >
                    <Check
                      size={20}
                      className="mt-0.5 flex-shrink-0"
                      style={{ color: plan.color }}
                    />
                    <span style={{ color: '#283750' }}>{feature}</span>
                  </motion.li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                className="w-full font-medium transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: plan.highlighted ? plan.color : 'transparent',
                  color: plan.highlighted ? 'white' : plan.color,
                  border: `2px solid ${plan.color}`,
                }}
              >
                {plan.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
